import { useCallback } from 'react';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';

interface IUseAllocationChangeTrackingOptions<T extends FieldValues> {
  changedField: Path<T>;
  deletedField: Path<T>;
}

const useAllocationChangeTracking = <T extends FieldValues>({
  changedField,
  deletedField,
}: IUseAllocationChangeTrackingOptions<T>) => {
  const { getValues, setValue } = useFormContext<T>();

  const appendUniqueId = useCallback(
    (field: Path<T>, allocationId: number) => {
      const currentIds = (getValues(field) as number[]) || [];

      setValue(field, Array.from(new Set([...currentIds, allocationId])) as PathValue<T, Path<T>>);
    },
    [getValues, setValue],
  );

  const markChanged = useCallback(
    (allocationId: number) => {
      appendUniqueId(changedField, allocationId);
    },
    [appendUniqueId, changedField],
  );

  const markDeleted = useCallback(
    (allocationId: number) => {
      appendUniqueId(deletedField, allocationId);
      const currentChangedIds = (getValues(changedField) as number[]) || [];
      setValue(
        changedField,
        currentChangedIds.filter(id => id !== allocationId) as PathValue<T, Path<T>>,
      );
    },
    [appendUniqueId, changedField, deletedField, getValues, setValue],
  );

  const unmarkDeleted = useCallback(
    (allocationId: number) => {
      const currentIds = (getValues(deletedField) as number[]) || [];
      setValue(deletedField, currentIds.filter(id => id !== allocationId) as PathValue<T, Path<T>>);
    },
    [deletedField, getValues, setValue],
  );

  const unmarkChanged = useCallback(
    (allocationId: number) => {
      const currentIds = (getValues(changedField) as number[]) || [];
      setValue(changedField, currentIds.filter(id => id !== allocationId) as PathValue<T, Path<T>>);
    },
    [changedField, getValues, setValue],
  );

  return {
    markChanged,
    markDeleted,
    unmarkDeleted,
    unmarkChanged,
  };
};

export default useAllocationChangeTracking;
